import { BorderRadius, Spacing } from '@persona-id/design-tokens';
import { capitalize, compact, isEmpty } from 'lodash/fp';
import { ReactNode, useState } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import DiffStatusSticker from 'dashboard/apps/InquiryTemplateEditor/DiffStatusSticker';
import { EXPIRATION_INTERVAL_LABELS } from 'dashboard/apps/InquiryTemplateEditor/forms/FormSectionExpirationsConfig/FormSectionExpirationsConfig';
import { REQUIRE_SELFIE_MATCH_OPTIONS } from 'dashboard/apps/InquiryTemplateEditor/forms/FormSectionSelfieMatch/FormSectionSelfieMatch';
import {
  CombinedStepForTree,
  WorkflowVersionMap,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/types';
import { getDisplayNameForStep } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenEditor/utils';
import InquiryTemplateVersionDiffSummaryTree from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateVersionDiffSummaryTree';
import {
  ChangeValue,
  CountryCodeMap,
  HashDiff,
  IdClassesByCountryDiff,
  ObjectArrayDiff,
  PersonaObjectArrayDiff,
  SetDiff,
  StepDiff,
  StepsArrayDiff,
  ValueDiff,
  VerificationTemplateVersionArrayDiff,
  VerificationTemplateVersionDiff,
  VersionDiff,
  VersionDiffChangeType,
  WorkflowVersionArrayDiff,
  WorkflowVersionStepArrayDiff,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateVisualVersionDiff/types';
import { setDiffIsNotEmpty } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateVisualVersionDiff/utils';
import ToggleButton from 'dashboard/apps/InquiryTemplateEditor/ToggleButton';
import { ValueDiffLabel } from 'dashboard/apps/InquiryTemplateEditor/ValueDiffLabel';
import Tooltip from 'dashboard/components/Tooltip';
import PanZoomBox from 'dashboard/components-lib/PanZoomBox';
import { InquiryTemplate } from 'dashboard/models/inquiry-template';
import {
  InquiryTemplateVersion,
  InquiryTemplateVersionStep,
} from 'dashboard/models/inquiry-template-version';
import { RootState } from 'dashboard/redux/store';
import Card from 'design-system/components/Card';
import Spacer from 'design-system/components/Spacer';
import Table, {
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from 'design-system/components/Table';
import { Label, Paragraph } from 'design-system/components/Text';
import ToggleSlider from 'design-system/components/ToggleSlider';
import { HelperTooltip } from 'design-system/components/Tooltip';
import { CHECK_DISPLAY_CONFIG } from 'lib/constants/check';
import { COUNTRIES, CountryCode, INQUIRY_LANGUAGES_ENGLISH } from 'lib/constants/locales';
import useToggle from 'lib/hooks/useToggle';
import { CountryCodeLower } from 'lib/interfaces/verificationConfig';
import { captureException } from 'lib/reporting';
import { assertNever } from 'lib/utils/types';
import { VerificationStatus } from 'lib/verification';
import { CheckName } from 'lib/verificationCheck';
import { RedirectQueryStringConfig } from 'verify/models/inquiry';

const Subsection = styled.div<{ emptyLabel: boolean }>`
  overflow-wrap: break-word;
  line-height: 20px;
  white-space: pre-line;
  ${({ emptyLabel, theme }) => {
    if (emptyLabel) {
      return `
        color: ${theme.color.text.secondary};
      `;
    }
  }}
`;

const FlowContainer = styled('div')`
  height: 800px;
  border-radius: ${BorderRadius['Lg']};
  overflow: hidden;
`;

const TextRow = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin: ${Spacing[1]} 0;
`;

const TextRowValue = styled(Label)`
  width: 50%;
`;

const EnabledCheckLabelContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacing[1]};
`;

const NoChangesMadeContainer = styled.div`
  height: 30px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const ALLOWED_HOSTS_LABEL = 'Allowed Domains';
const DISABLE_CLIENT_CREATE_LABEL = 'Block client-side Inquiry creation';
const ENABLED_LOCALES_LABEL = 'Supported Languages';
const ENABLED_COUNTRIES_LABEL = 'Enabled Countries and IDs';
const SELFIE_MATCH_LABEL = 'Selfie Poses';
const EXPIRATION_INTERVALS_LABEL = 'Expiration Intervals';
const REDIRECT_URI_LABEL = 'Redirect URI';
const REDIRECT_QUERY_STRING_CONFIG_LABEL = 'Redirect Query String Config';
const FIELD_SCHEMA_LABEL = 'Field Schema';
const VERIFICATIONS_LABEL = 'Verifications';
const DEVICE_HANDOFF_OPTIONS_LABEL = 'Device Handoff Options';
const CAPTURE_METHODS_LABEL = 'Capture Methods';
const THEME_LABEL = 'Theme';
const ACTION_STEPS_LABEL = 'Action Steps';
const COUNTRY_SELECT_LABEL = 'Country Select Options';
const VERIFICATION_CHECKS_LABEL = 'Verification Checks';
const NOT_AVAILABLE_LABEL = 'n/a';
export const EMPTY_LABEL = '--';
const STEPS_SCREEN_LABEL = 'Screens';

const REQUIRED_CHECK_STATUSES = [VerificationStatus.Failed, VerificationStatus.RequiresRetry];

const generateEmptySummaryRowConfig = (title: ReactNode): SummaryRowConfig => ({
  title: title,
  newLabel: EMPTY_LABEL,
  removedLabel: EMPTY_LABEL,
  modifiedLabel: EMPTY_LABEL,
});

interface SummaryRowConfig {
  title: ReactNode;
  newLabel: string | null;
  removedLabel: string | null;
  modifiedLabel: string | null;
}

/*
 * This function is used to format the summary row config for a set diff
 * @param title - title of the section
 * @param setDiff - set diff object
 * @param mapFn - function to map the diff item to a string
 * @param newLine - boolean to determine whether a new line is added after each item
 */
function getSummarySectionLabelsFromSetDiff<T>({
  title,
  setDiff,
  mapFn,
  newLine = false,
}: {
  title: ReactNode;
  setDiff: SetDiff<T>;
  mapFn: (diffItem: T) => string;
  newLine?: boolean;
}): SummaryRowConfig {
  const diffNotEmpty = setDiffIsNotEmpty(setDiff);
  return {
    title,
    newLabel: diffNotEmpty ? setDiff.added.map(mapFn).join(newLine ? ',\n' : ', ') : null,
    removedLabel: diffNotEmpty ? setDiff.removed.map(mapFn).join(newLine ? ',\n' : ', ') : null,
    modifiedLabel: NOT_AVAILABLE_LABEL,
  };
}

/*
 * This function is used to format the summary row config for a hash/hashSet diff
 * @param title - title of the section
 * @param addedValues - array of added values
 * @param removedValues - array of removed values
 * @param modifiedValues - array of modified values
 * @param mapFn - function to map the diff item to a string
 * @param allowDuplicates - boolean to allow duplicates in the added and removed values
 */
function formatSummaryRowConfig<T>({
  title,
  addedValues,
  removedValues,
  modifiedValues,
  mapFn,
  allowDuplicates,
}: {
  title: ReactNode;
  addedValues: T[];
  removedValues: T[];
  modifiedValues: T[];
  mapFn: (diffItem: T) => string;
  allowDuplicates: boolean;
}): SummaryRowConfig {
  let addedValuesMapped = [...new Set(addedValues.map(mapFn))];
  let removedValuesMapped = [...new Set(removedValues.map(mapFn))];
  const modifiedValuesMapped = [...new Set(modifiedValues.map(mapFn))];
  if (!allowDuplicates) {
    addedValuesMapped = addedValuesMapped.filter((x) => !modifiedValuesMapped.includes(x));
    removedValuesMapped = removedValuesMapped.filter((x) => !modifiedValuesMapped.includes(x));
  }
  return {
    title,
    newLabel: addedValuesMapped.length > 0 ? addedValuesMapped.join(', ') : null,
    removedLabel: removedValuesMapped.length > 0 ? removedValuesMapped.join(', ') : null,
    modifiedLabel: modifiedValuesMapped.length > 0 ? modifiedValuesMapped.join(', ') : null,
  };
}

function filterHashByVersionDiffType<T extends string | number | symbol>(
  hash: HashDiff<T>,
  changeType: VersionDiffChangeType,
): T[] {
  return Object.entries(hash)
    .filter(([, value]) => (value as ChangeValue)['change-type'] === changeType)
    .map(([key]) => key as T);
}

/*
 * This function is used to format the summary row config for a hash diff
 * @param title - title of the section
 * @param hash - hash diff object
 * @param mapFn - function to map the diff item to a string
 * This function is used for the following properties:
 * - Expire Intervals
 * - Theme
 * - Enabled Countries and IDs
 * -
 */
function getSummarySectionLabelsFromHashDiff<T extends string | number | symbol>(
  title: ReactNode,
  hash: HashDiff<T> | null,
  mapFn: (diffItem: T) => string,
): SummaryRowConfig | null {
  if (hash == null) return generateEmptySummaryRowConfig(title);

  return formatSummaryRowConfig({
    title,
    addedValues: filterHashByVersionDiffType(hash, VersionDiffChangeType.ADDED),
    removedValues: filterHashByVersionDiffType(hash, VersionDiffChangeType.REMOVED),
    modifiedValues: filterHashByVersionDiffType(hash, VersionDiffChangeType.MODIFIED),
    mapFn,
    allowDuplicates: false,
  });
}

function filterObjectArrayByVersionDiffType<T>(
  objectArray: ObjectArrayDiff<T> | PersonaObjectArrayDiff,
  changeType: VersionDiffChangeType,
  key: keyof T,
): T[keyof T][] {
  return Object.values(objectArray)
    .filter((object) => object['change-type'] === changeType)
    .map((object) => {
      if (object['change-type'] === VersionDiffChangeType.MODIFIED) {
        return object.meta['target-value'][key];
      } else {
        return object.meta.value[key];
      }
    });
}

/*
 * This function is used to format the summary row config for a hashSet diff
 * @param title - title of the section
 * @param hashSet - hashSet diff object
 * @param mapFn - function to map the diff item to a string
 * This function is used for the following properties:
 * - Field Schema
 * - Enabled Checks
 * - Required Checks
 * - Country Select Options
 * - Device Handoff Options
 * - Capture Methods
 */
function getSummarySectionLabelsFromObjectArrayDiff<T>(
  title: ReactNode,
  objectArray: ObjectArrayDiff<T>,
  mapFn: (diffItem: T[keyof T]) => string,
  key: keyof T,
): SummaryRowConfig {
  return formatSummaryRowConfig({
    title,
    addedValues: filterObjectArrayByVersionDiffType(objectArray, VersionDiffChangeType.ADDED, key),
    removedValues: filterObjectArrayByVersionDiffType(
      objectArray,
      VersionDiffChangeType.REMOVED,
      key,
    ),
    modifiedValues: filterObjectArrayByVersionDiffType(
      objectArray,
      VersionDiffChangeType.MODIFIED,
      key,
    ),
    mapFn,
    allowDuplicates: true,
  });
}

function getSummarySectionLabelsFromStepsArrayDiff(
  stepsArrayDiff: StepsArrayDiff,
): SummaryRowConfig {
  return formatSummaryRowConfig({
    title: STEPS_SCREEN_LABEL,
    addedValues: filterStepDiffByVersionDiffType(stepsArrayDiff, VersionDiffChangeType.ADDED),
    removedValues: filterStepDiffByVersionDiffType(stepsArrayDiff, VersionDiffChangeType.REMOVED),
    modifiedValues: filterStepDiffByVersionDiffType(stepsArrayDiff, VersionDiffChangeType.MODIFIED),
    mapFn: (stepName: string) => stepName,
    allowDuplicates: true,
  });
}

function filterStepDiffByVersionDiffType(
  StepsArrayDiff: StepsArrayDiff,
  changeType: VersionDiffChangeType,
) {
  return Object.values(StepsArrayDiff)
    .filter((stepDiff) => stepDiff['change-type'] === changeType)
    .map((stepDiff) => stepDiff.meta['name-display']);
}

// Filters the verification template versions by change type and returns the name of the verification template version
function filterVerificationTemplateVersionDiffByVersionDiffType(
  verificationTemplateVersions: VerificationTemplateVersionArrayDiff,
  changeType: VersionDiffChangeType,
): string[] {
  const matchingVerificationTemplateVersions = Object.values(verificationTemplateVersions).filter(
    (verificationTemplateVersionDiff) =>
      verificationTemplateVersionDiff['change-type'] === changeType,
  );
  return matchingVerificationTemplateVersions.map((vtv) => vtv.meta.name);
}

// Formats the summary row config for the verification template versions
function getSummarySectionLabelFromVerificationDiff(
  verificationTemplateVersions: VerificationTemplateVersionArrayDiff,
): SummaryRowConfig | null {
  if (verificationTemplateVersions == null) return null;
  return formatSummaryRowConfig({
    title: VERIFICATIONS_LABEL,
    addedValues: filterVerificationTemplateVersionDiffByVersionDiffType(
      verificationTemplateVersions,
      VersionDiffChangeType.ADDED,
    ),
    removedValues: filterVerificationTemplateVersionDiffByVersionDiffType(
      verificationTemplateVersions,
      VersionDiffChangeType.REMOVED,
    ),
    modifiedValues: filterVerificationTemplateVersionDiffByVersionDiffType(
      verificationTemplateVersions,
      VersionDiffChangeType.MODIFIED,
    ),
    mapFn: (verificationTemplateVersionDiff) => verificationTemplateVersionDiff,
    allowDuplicates: true,
  });
}

// Formats the summary row config for the country codes
function getSummarySectionLabelFromCountryCodes(
  countryCodes: CountryCodeMap,
  steps: InquiryTemplateVersionStep[],
): SummaryRowConfig[] {
  return Object.entries(countryCodes).map(([countryCodeId, countryCodes]) => {
    const [stepName, componentName] = countryCodeId.split(':');
    const step = steps.find((step) => step.name === stepName);
    const stepDisplayName = getDisplayNameForStep(step);
    return getSummarySectionLabelsFromSetDiff({
      title: `${COUNTRY_SELECT_LABEL}: ${stepDisplayName}, ${componentName}`,
      setDiff: countryCodes,
      mapFn: (countryCode) => COUNTRIES[countryCode],
    });
  });
}

export function formatStepPropertyValues(value: string): string {
  // capitalizes all words and replaces underscores with spaces
  return value
    .split(':')
    .map((value) => capitalize(value.replace(/_/g, ' ')))
    .join(':');
}
function returnSummarySectionLabelsForStepProperty(
  step: StepDiff,
  property: 'device-handoff-options' | 'capture-methods',
  displayName: string,
): SummaryRowConfig | null {
  if (!(step.changes[property] && setDiffIsNotEmpty(step.changes[property]))) return null;
  return getSummarySectionLabelsFromSetDiff({
    title: step.meta['name-display'] + `: ${displayName}`,
    setDiff: step.changes[property],
    mapFn: (value) => formatStepPropertyValues(value),
    newLine: true,
  });
}

/*
 * Formats the summary row config for steps
 * for every step it checks whether there are any changes to the device handoff options or capture methods
 * if there are changes, it adds a new summary row config
 */
function getSummarySectionLabelFromCombinedSteps(steps: StepsArrayDiff): SummaryRowConfig[] {
  const stepSummaryLabels: (SummaryRowConfig | null)[] = [];
  Object.values(steps).flatMap((step) => {
    stepSummaryLabels.push(
      returnSummarySectionLabelsForStepProperty(
        step,
        'device-handoff-options',
        DEVICE_HANDOFF_OPTIONS_LABEL,
      ),
    );
    stepSummaryLabels.push(
      returnSummarySectionLabelsForStepProperty(step, 'capture-methods', CAPTURE_METHODS_LABEL),
    );
  });

  return compact(stepSummaryLabels);
}

const filterUpdateNextStepFromWorkflowSteps = (workflowSteps: string[]) => {
  return workflowSteps.filter((workflowStep) => workflowStep !== 'Update next step');
};

function getSummarySectionLabelFromValueDiff<T extends string | number | boolean = string>(
  title: string,
  valueDiff: ValueDiff<T> | null,
  mapFn: (value: T) => string,
): SummaryRowConfig | null {
  if (valueDiff == null) return null;

  return {
    title,
    newLabel: valueDiff.added ? mapFn(valueDiff.added) : null,
    removedLabel: valueDiff.removed ? mapFn(valueDiff.removed) : null,
    modifiedLabel: NOT_AVAILABLE_LABEL,
  };
}

/*
 * Filter steps config changes that only contain path changes.
 * We don't need to provide such granular details to the visual version diff table
 * as users can see the positional changes in the tree view
 */
const filterStepsConfigContainingOnlyPathChanges = (
  stepsConfig: WorkflowVersionStepArrayDiff,
): WorkflowVersionStepArrayDiff => {
  return Object.fromEntries(
    Object.entries(stepsConfig).filter(([_, stepConfig]) => {
      // if a step is added or removed, we always render its changes
      if (stepConfig['change-type'] !== VersionDiffChangeType.MODIFIED) return true;
      const containsConditionalChanges = stepConfig.changes.conditionals != null;
      const containsNonPathChanges =
        Object.keys(stepConfig.changes.step).length > 1 || !('path' in stepConfig.changes.step);
      return containsConditionalChanges || containsNonPathChanges;
    }),
  );
};
/*
 * Formats the summary row config for workflow versions
 * We only care about the steps config for workflow versions. If a workflow version is added or removed,
 * we mark all of its steps as added or removed. If a workflow version is modified, we individually
 * check each step to see if it was added, removed, or modified.
 */
function getSummarySectionLabelFromWorkflowVersions(
  workflowVersionDiffMap: WorkflowVersionArrayDiff,
): SummaryRowConfig {
  const modifiedWorkflowSteps: string[] = [];
  const addedWorkflowSteps: string[] = [];
  const removedWorkflowSteps: string[] = [];

  Object.values(workflowVersionDiffMap).forEach((workflowVersionDiff) => {
    // if a workflow version is modified, we check each step to see if it was added, removed, or modified
    switch (workflowVersionDiff['change-type']) {
      case VersionDiffChangeType.MODIFIED:
        modifiedWorkflowSteps.push(
          ...filterObjectArrayByVersionDiffType(
            filterStepsConfigContainingOnlyPathChanges(workflowVersionDiff.changes['steps-config']),
            VersionDiffChangeType.MODIFIED,
            'name',
          ),
        );
        addedWorkflowSteps.push(
          ...filterObjectArrayByVersionDiffType(
            workflowVersionDiff.changes['steps-config'],
            VersionDiffChangeType.ADDED,
            'name',
          ),
        );
        removedWorkflowSteps.push(
          ...filterObjectArrayByVersionDiffType(
            workflowVersionDiff.changes['steps-config'],
            VersionDiffChangeType.REMOVED,
            'name',
          ),
        );
        break;
      case VersionDiffChangeType.ADDED:
        // if a workflow version is added, we mark all of its steps as added
        addedWorkflowSteps.push(...workflowVersionDiff.meta['step-names']);
        break;
      case VersionDiffChangeType.REMOVED:
        // if a workflow version is removed, we mark all of its steps as removed
        removedWorkflowSteps.push(...workflowVersionDiff.meta['step-names']);
        break;
      default:
        assertNever(workflowVersionDiff['change-type']);
    }
  });

  return formatSummaryRowConfig({
    title: ACTION_STEPS_LABEL,
    addedValues: filterUpdateNextStepFromWorkflowSteps(addedWorkflowSteps),
    removedValues: filterUpdateNextStepFromWorkflowSteps(removedWorkflowSteps),
    modifiedValues: filterUpdateNextStepFromWorkflowSteps(modifiedWorkflowSteps),
    mapFn: (value) => value,
    allowDuplicates: true,
  });
}

const EnabledCheckLabel = (): ReactNode => {
  return (
    <EnabledCheckLabelContainer>
      {VERIFICATION_CHECKS_LABEL}
      <HelperTooltip side='top' interactive>
        Required Checks are marked with an asterisk (*)
      </HelperTooltip>
    </EnabledCheckLabelContainer>
  );
};

const getSummarySectionLabelsFromEnabledIdClassesByCountry = (
  idClassesByCountryArrayDiff: IdClassesByCountryDiff | null,
) => {
  if (!idClassesByCountryArrayDiff) return null;
  return formatSummaryRowConfig({
    title: ENABLED_COUNTRIES_LABEL,
    addedValues: filterObjectArrayByVersionDiffType(
      idClassesByCountryArrayDiff,
      VersionDiffChangeType.ADDED,
      'country',
    ),
    removedValues: filterObjectArrayByVersionDiffType(
      idClassesByCountryArrayDiff,
      VersionDiffChangeType.REMOVED,
      'country',
    ),
    modifiedValues: filterObjectArrayByVersionDiffType(
      idClassesByCountryArrayDiff,
      VersionDiffChangeType.MODIFIED,
      'country',
    ),
    mapFn: (countryCode) => {
      // country codes are sent over in lowercase, where as the COUNTRIES
      // record is in uppercase.
      const countryCodeKey = (countryCode as CountryCodeLower).toUpperCase();
      if (countryCodeKey.toUpperCase() in COUNTRIES) {
        return COUNTRIES[countryCodeKey as CountryCode];
      }

      // Log missing country code to Sentry and return the country code key
      captureException(new Error(`Invalid country code: ${countryCodeKey}`));
      return countryCodeKey;
    },
    allowDuplicates: true,
  });
};

/*
 *  Verification Summary Rows
 *  Note that every verification generates a new section in the table
 *  The following properties are displayed in each verification summary table:
 * - Enabled Checks
 * - Required Checks
 * - Enabled Countries and IDs
 */
const generateVerificationSummaryRows = (
  verificationTemplateVersionDiff: VerificationTemplateVersionDiff,
  showUnchanged: boolean,
) => {
  const summarylabels = compact([
    getSummarySectionLabelsFromObjectArrayDiff(
      EnabledCheckLabel(),
      verificationTemplateVersionDiff.changes['verification-checks'],
      (check) => {
        if ((check as CheckName) in CHECK_DISPLAY_CONFIG) {
          return (
            CHECK_DISPLAY_CONFIG[check as CheckName].name +
            (isRequiredCheck(check as CheckName, verificationTemplateVersionDiff) ? '*' : '')
          );
        }

        // Log missing check name to Sentry and return a default check name
        captureException(new Error(`Invalid check name: ${check}`));
        return (check as string).replace(/_/g, ' ') + ' check';
      },
      'check',
    ),
    getSummarySectionLabelsFromEnabledIdClassesByCountry(
      verificationTemplateVersionDiff.changes['enabled-id-classes-by-country'],
    ),
    getSummarySectionLabelFromValueDiff(
      SELFIE_MATCH_LABEL,
      verificationTemplateVersionDiff.changes['selfie-match'],
      (selfieMatch) => {
        const selfieMatchOption = REQUIRE_SELFIE_MATCH_OPTIONS.find(
          (option) => option.value === selfieMatch,
        );
        if (selfieMatchOption) {
          return selfieMatchOption.label;
        }

        // Log missing selfie match option to Sentry and return a default selfie match option
        captureException(new Error(`Invalid selfie match option: ${selfieMatch}`));
        return selfieMatch.replace(/_/g, ' ') + ' pose';
      },
    ),
  ]);

  return (
    <>
      <section>
        {generateTextRow(
          'Template Name',
          <TextRowValue>
            <ValueDiffLabel
              valueDiff={verificationTemplateVersionDiff.changes.name}
              fallbackValue={verificationTemplateVersionDiff.meta.name}
            />
          </TextRowValue>,
        )}
        <Spacer height={2} />
        <DiffSummaryTable
          ariaLabel='Flow diff summary table'
          sections={summarylabels}
          showUnchanged={showUnchanged}
        />
        <Spacer />
      </section>
    </>
  );
};

const isRequiredCheck = (
  check: CheckName,
  verificationTemplateVersionDiff: VerificationTemplateVersionDiff,
): boolean => {
  const checkChanges =
    verificationTemplateVersionDiff.changes['verification-checks'][check as CheckName];
  const onFailed =
    checkChanges['change-type'] === VersionDiffChangeType.MODIFIED
      ? checkChanges.meta['target-value']['on-failed']
      : checkChanges.meta.value['on-failed'];
  return onFailed != null && REQUIRED_CHECK_STATUSES.includes(onFailed);
};

const generateTextRow = (label: string, value: ReactNode) => {
  return (
    <TextRow>
      <TextRowValue>
        <Label color='secondary' size='md'>
          {label}
        </Label>
      </TextRowValue>
      <TextRowValue>{value}</TextRowValue>
    </TextRow>
  );
};

interface InquiryTemplateVersionDiffSummaryProps {
  versionDiff: VersionDiff;
  inquiryTemplateVersion: InquiryTemplateVersion;
  targetWorkflowVersionMap: WorkflowVersionMap;
  baseWorkflowVersionMap: WorkflowVersionMap;
  targetStepsTree: CombinedStepForTree[];
  baseStepsTree: CombinedStepForTree[];
}
const InquiryTemplateVersionDiffSummary = ({
  versionDiff,
  inquiryTemplateVersion,
  targetWorkflowVersionMap,
  baseWorkflowVersionMap,
  targetStepsTree,
  baseStepsTree,
}: InquiryTemplateVersionDiffSummaryProps) => {
  const [showCurrentVersionTree, setShowCurrentVersionTree] = useState(true);
  const [showUnchanged, { toggle: toggleShowUnchanged }] = useToggle(false);
  const inquiryTemplate = useSelector<RootState, InquiryTemplate | undefined>(
    (state) => state.inquiryTemplate.inquiryTemplate as InquiryTemplate,
  );

  const {
    // TODO: device handoff attributes are missing; add them
    'allowed-hosts': allowedHosts,
    'disable-client-create': disableClientCreate,
    'enabled-locales': enabledLocales,
    'country-codes': countryCodes,
    'expiration-intervals': expirationIntervals,
    'field-schema': fieldSchema,
    'redirect-uri': redirectUri,
    'redirect-query-string-config': redirectQueryStringConfig,
    'verification-template-versions': verificationTemplateVersions,
    steps: steps,
    'workflow-versions': workflowVersions,
    theme,
    connectors,
  } = versionDiff.inquiryTemplateVersionDiff;

  const noTreeChanges = [
    connectors.base.modified,
    connectors.base.removed,
    connectors.target.added,
    connectors.target.modified,
    steps,
    workflowVersions,
  ].every(isEmpty);

  const {
    name,
    'last-updater': lastUpdater,
    steps: inquiryTemplateVersionSteps,
  } = inquiryTemplateVersion.attributes;
  const modifiedVerificationTemplateVersions = Object.values(verificationTemplateVersions).filter(
    (verificationTemplateVersion) =>
      verificationTemplateVersion['change-type'] === VersionDiffChangeType.MODIFIED,
  );

  /*
   * Config Summary Rows
   * The following properties are displayed in the config summary table:
   * - Allowed Hosts
   * - Disable Client Create
   * - Supported Languages
   * - Country Select Options
   * - Expiration Intervals
   * - Field Schema
   * - Verification Template Versions
   * - Device Handoff Options
   * - Capture Methods
   * - Redirect Configurations
   */
  const configSummaryRows: SummaryRowConfig[] = compact([
    getSummarySectionLabelsFromSetDiff({
      title: ALLOWED_HOSTS_LABEL,
      setDiff: allowedHosts,
      mapFn: (s) => s,
    }),
    getSummarySectionLabelFromValueDiff(DISABLE_CLIENT_CREATE_LABEL, disableClientCreate, (s) =>
      s ? 'Blocking enabled' : 'Blocking disabled',
    ),
    getSummarySectionLabelsFromSetDiff({
      title: ENABLED_LOCALES_LABEL,
      setDiff: enabledLocales,
      mapFn: (locale) => INQUIRY_LANGUAGES_ENGLISH[locale],
    }),
    ...getSummarySectionLabelFromCountryCodes(countryCodes, inquiryTemplateVersionSteps),
    getSummarySectionLabelsFromHashDiff(
      EXPIRATION_INTERVALS_LABEL,
      expirationIntervals,
      (expirationInterval) => EXPIRATION_INTERVAL_LABELS[expirationInterval],
    ),
    getSummarySectionLabelFromValueDiff(REDIRECT_URI_LABEL, redirectUri, (s) => s),
    getSummarySectionLabelsFromHashDiff(
      REDIRECT_QUERY_STRING_CONFIG_LABEL,
      redirectQueryStringConfig,
      (diffItem) => {
        const labels: Record<keyof RedirectQueryStringConfig, string> = {
          'include-inquiry-id': 'Include inquiry ID',
          'include-reference-id': 'Include reference ID',
          'include-status': 'Include status',
          'include-fields': 'Include fields',
          'field-keys': 'Field keys',
        };

        return labels[diffItem];
      },
    ),
    getSummarySectionLabelsFromObjectArrayDiff(
      FIELD_SCHEMA_LABEL,
      fieldSchema,
      (field) => field,
      'label',
    ),
    getSummarySectionLabelFromVerificationDiff(verificationTemplateVersions),
    ...getSummarySectionLabelFromCombinedSteps(steps),
  ]);

  /*
   * Flow Summary Rows
   * The following properties are displayed in the flow summary table:
   * - Theme
   * - Action Steps (Workflow Steps)
   */
  const flowSummaryRows: SummaryRowConfig[] = compact([
    getSummarySectionLabelsFromStepsArrayDiff(steps),
    getSummarySectionLabelFromWorkflowVersions(workflowVersions),
    getSummarySectionLabelsFromHashDiff(THEME_LABEL, theme, (themeAttribute) =>
      capitalize(themeAttribute.replace(/-/g, ' ')),
    ),
  ]);
  return (
    <Card>
      <Card.Body>
        <Card.Title
          data-test='diff-summary-title'
          actions={[
            <ToggleSlider
              key='show-unchanged-properties'
              titleOn='displaying all properties'
              titleOff='only showing changed properties'
              labelOn='Show all properties'
              labelOff='Show all properties'
              value={showUnchanged}
              onChange={toggleShowUnchanged}
            />,
          ]}
        >
          Summary of changes
        </Card.Title>
        <Card.CollapsibleSection defaultExpanded={!noTreeChanges} title='Flow'>
          <ToggleButton
            inactiveButtonLabel='Last Published Version'
            activeButtonLabel='Current Draft Version'
            setIsActive={setShowCurrentVersionTree}
            isActive={showCurrentVersionTree}
          />
          <FlowContainer>
            <PanZoomBox
              preventPanOnScroll
              withDynamicUpdates
              zoomToFit
              zoomToFitPadding={{ top: 30, right: 30, bottom: 30, left: 30 }}
            >
              {showCurrentVersionTree ? (
                <InquiryTemplateVersionDiffSummaryTree
                  inquiryTemplateVersion={inquiryTemplateVersion}
                  workflowVersionMap={targetWorkflowVersionMap}
                  tree={targetStepsTree}
                  stepArrayDiff={steps}
                  workflowVersionArrayDiff={workflowVersions}
                  connectorDiff={connectors.target}
                />
              ) : (
                <InquiryTemplateVersionDiffSummaryTree
                  inquiryTemplateVersion={inquiryTemplateVersion}
                  workflowVersionMap={baseWorkflowVersionMap}
                  tree={baseStepsTree}
                  stepArrayDiff={steps}
                  workflowVersionArrayDiff={workflowVersions}
                  connectorDiff={connectors.base}
                />
              )}
            </PanZoomBox>
            <Spacer height={2} />
          </FlowContainer>
        </Card.CollapsibleSection>
        <section>
          {generateTextRow('Template Name', name)}
          {generateTextRow('Last Modified By', lastUpdater)}
          {inquiryTemplate?.attributes?.description &&
            generateTextRow('Description', inquiryTemplate?.attributes.description)}
          <Spacer height={2} />
          <Card.SectionHeader>Configurations</Card.SectionHeader>
          <DiffSummaryTable
            ariaLabel='Config diff summary table'
            sections={configSummaryRows}
            showUnchanged={showUnchanged}
          />
        </section>
        <Spacer />
        {modifiedVerificationTemplateVersions.length > 0 && (
          <>
            <section>
              <Card.SectionHeader>Verifications</Card.SectionHeader>
              {modifiedVerificationTemplateVersions.map((vtv) =>
                generateVerificationSummaryRows(vtv, showUnchanged),
              )}
            </section>
          </>
        )}
        <section>
          <Card.SectionHeader>Flow and experience</Card.SectionHeader>
          <DiffSummaryTable
            ariaLabel='Flow diff summary table'
            sections={flowSummaryRows}
            showUnchanged={showUnchanged}
          />
        </section>
      </Card.Body>
    </Card>
  );
};

const SummarySubsection = ({ text }: { text: string | null }) => {
  let sectionLabel = text ?? EMPTY_LABEL;
  let overflowText = null;
  if (sectionLabel.split(',').length > 10) {
    overflowText = sectionLabel.split(',').slice(10);
    sectionLabel = sectionLabel.split(',').slice(0, 10).join(', ');
  }
  return (
    <Subsection emptyLabel={[EMPTY_LABEL, NOT_AVAILABLE_LABEL].includes(sectionLabel)}>
      {sectionLabel}
      {overflowText && (
        <Tooltip
          trigger='hover'
          tooltip={
            <Paragraph color='primary' size='sm'>
              {overflowText.join(', ')}
            </Paragraph>
          }
          placement='bottom-end'
          delayShow={200}
        >
          ,<Label color='secondary'> +{overflowText.length} more</Label>
        </Tooltip>
      )}
    </Subsection>
  );
};

const isEmptySummaryRowConfig = (summaryRowConfig: SummaryRowConfig): boolean => {
  const { newLabel, removedLabel, modifiedLabel } = summaryRowConfig;
  const emptyLabels = [EMPTY_LABEL, NOT_AVAILABLE_LABEL];
  return (
    (newLabel == null || emptyLabels.includes(newLabel)) &&
    (removedLabel == null || emptyLabels.includes(removedLabel)) &&
    (modifiedLabel == null || emptyLabels.includes(modifiedLabel))
  );
};

const DiffSummaryTable = ({
  ariaLabel,
  sections,
  showUnchanged,
}: {
  ariaLabel: string;
  sections: SummaryRowConfig[];
  showUnchanged: boolean;
}) => {
  const filteredSections = sections.filter(
    (section) => !isEmptySummaryRowConfig(section) || showUnchanged,
  );
  if (isEmpty(filteredSections)) {
    return (
      <NoChangesMadeContainer>
        <Label color='secondary' size='md'>
          No changes made
        </Label>
      </NoChangesMadeContainer>
    );
  }

  return (
    <div>
      <Table aria-label={ariaLabel} style={{ tableLayout: 'fixed' }}>
        <TableHead>
          <TableRow>
            <TableHeaderCell>Sections</TableHeaderCell>
            <TableHeaderCell>
              <DiffStatusSticker changeType={VersionDiffChangeType.ADDED} />
            </TableHeaderCell>
            <TableHeaderCell>
              <DiffStatusSticker changeType={VersionDiffChangeType.REMOVED} />
            </TableHeaderCell>
            <TableHeaderCell>
              <DiffStatusSticker changeType={VersionDiffChangeType.MODIFIED} />
            </TableHeaderCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {filteredSections.map(({ title, newLabel, removedLabel, modifiedLabel }, index) => (
            <TableRow key={index} style={{ backgroundColor: 'transparent' }}>
              <TableCell size='md' breakWord>
                {title}
              </TableCell>
              <TableCell size='md' breakWord>
                <SummarySubsection text={newLabel} />
              </TableCell>
              <TableCell size='md' breakWord>
                <SummarySubsection text={removedLabel} />
              </TableCell>
              <TableCell size='md' breakWord>
                <SummarySubsection text={modifiedLabel} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

export default InquiryTemplateVersionDiffSummary;
